import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import FilterComponent from "../../components/FilterComponent/FilterComponent";
import ResultCards from "../../components/Result-Cards/ResultCards";
import { ShimmerContentBlock } from "react-shimmer-effects";
import "./ResultsPage.css";
import "../../assets/css/mobile.css";
import axios from "axios";

const ResultsPage = () => {
  const location = useLocation();
  const token = localStorage.getItem("userToken");

  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuggestion, setSuggestion] = useState(false);
  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const [fromMeasure, setFromMeasure] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);

  const [selectedFilters, setSelectedFilters] = useState({
    shape: [],
    color: [],
    vendor: [],
    teethCase: [],
    width: "",
    height: "",
    mass: "",
    alphaCode: "",
  });

  /* Drives the empty state: "no results for your filters" and "nothing here
     at all" are different situations and need different copy. */
  const hasActiveFilters = Object.values(selectedFilters).some((value) =>
    Array.isArray(value) ? value.length > 0 : Boolean(value)
  );

  const clearFilters = () =>
    setSelectedFilters({
      shape: [],
      color: [],
      vendor: [],
      teethCase: [],
      width: "",
      height: "",
      mass: "",
      alphaCode: "",
    });

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const from = queryParams.get("from");
    if (from === "measure") {
      setFromMeasure(true);
      const image = localStorage.getItem("capturedImage");
      setCapturedImage(image);
    }
  }, [location.search]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);

    const safeParseArray = (param) => {
      try {
        const value = queryParams.get(param);
        return value ? JSON.parse(value) : [];
      } catch {
        return [];
      }
    };

    const safeParseString = (param) => {
      const value = queryParams.get(param);
      return value && typeof value === "string" ? value.trim() : "";
    };

    // Special handling for alphaCode to ensure it's not truncated
    const safeParseAlphaCode = (param) => {
      const value = queryParams.get(param);
      if (!value) return "";

      // Decode URL encoding if present
      const decoded = decodeURIComponent(value);

      return decoded && typeof decoded === "string" ? decoded.trim() : "";
    };

    const width = safeParseString("width");
    const height = safeParseString("height");
    const mass = safeParseString("mass");
    const color = safeParseArray("color");
    const shape = safeParseArray("shape");
    const vendor = safeParseArray("vendor");
    const teethCase = safeParseArray("teethCase");
    const alphaCode = safeParseAlphaCode("alphaCode");

    const newFilters = {
      shape,
      color,
      vendor,
      teethCase,
      width,
      height,
      mass,
      alphaCode,
    };

    setSelectedFilters(newFilters);

    setFiltersInitialized(true); // ✅ Only trigger fetch after this
  }, [location.search]);

  // Fetch filtered products
  const fetchData = async () => {
    if (!token) return;
    setIsLoading(true);

    const requestData = {
      width: selectedFilters.width,
      height: selectedFilters.height,
      color: selectedFilters.color,
      shape: selectedFilters.shape,
      vendor: selectedFilters.vendor,
      teethCase: selectedFilters.teethCase,
      mass: selectedFilters.mass,
      alphaCode: selectedFilters.alphaCode,
    };

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/auth/v1/products`,
        requestData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const safeProducts = Array.isArray(response.data.Products)
        ? response.data.Products.filter((p) => p && typeof p === "object")
        : [];

      setProducts(safeProducts);
      setSuggestion(response.data.isSuggestion || false);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Run fetch only after filters are initialized
  useEffect(() => {
    if (!filtersInitialized) return;
    fetchData();
  }, [selectedFilters, token, filtersInitialized]);

  return (
    <div className="Results-Page-Wrapper">
      {/* The page head states what came back, not a slogan. "Discover top
          searches here" sat under every result set regardless of what was
          in it, and a centred title-over-subtitle stack is the section head
          on a million pages - the count is the fact the user is here for,
          so it is the thing given weight. */}
      <div className="results-head">
        <h1 className="results-title">Search results</h1>
        <p className="results-count">
          {isLoading
            ? "Searching\u2026"
            : products.length === 0
            ? "No moulds matched"
            : `${products.length} ${
                products.length === 1 ? "mould" : "moulds"
              } matched`}
          {isSuggestion && products.length > 0 && (
            <span className="results-note">
              {" "}
              &mdash; nothing matched exactly, showing the closest moulds
            </span>
          )}
        </p>
      </div>

      <div className="results-wrapper">
        <FilterComponent
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          mass={selectedFilters.mass}
          width={selectedFilters.width}
          height={selectedFilters.height}
          alphaCode={selectedFilters.alphaCode}
        />

        <div className="results-section">
          <div className="results-cards">
            {isLoading ? (
              <div className="shimmer-loader">
                <ShimmerContentBlock
                  title
                  text
                  cta
                  thumbnailWidth={370}
                  thumbnailHeight={370}
                />
              </div>
            ) : products.length > 0 ? (
              products.map((product) =>
                product && product._id ? (
                  <ResultCards
                    key={product._id}
                    _id={product._id}
                    mass={product.mass}
                    vendor={product.vendor}
                    vendorSlug={product.vendorSlug}
                    title={product.name}
                    description={product.description}
                    color={product.color}
                    height={product.height}
                    width={product.width}
                    shape={product.shape}
                    casetype={product.case}
                    images={product.images}
                    createdDate={product.createdDate}
                    fromMeasure={fromMeasure}
                    measureImage={capturedImage}
                  />
                ) : null
              )
            ) : (
              /* The illustration here was a hardcoded S3 URL that no longer
                 resolves, so the empty state rendered as a broken-image
                 icon. An empty state does not need a picture: it needs to
                 say what happened and offer the way out. */
              <div className="notfound">
                {/* A caliper closed on nothing. The empty state carries the
                    same measuring motif as the rest of the product rather
                    than a generic magnifier, and it is drawn rather than
                    fetched - the illustration that used to sit here was a
                    hardcoded S3 URL that stopped resolving. */}
                <svg
                  className="notfound-mark"
                  viewBox="0 0 96 48"
                  aria-hidden="true"
                >
                  <line x1="8" y1="30" x2="88" y2="30" />
                  {Array.from({ length: 17 }, (_, i) => (
                    <line
                      key={i}
                      x1={8 + i * 5}
                      y1="30"
                      x2={8 + i * 5}
                      y2={i % 4 === 0 ? 37 : 34}
                      opacity={i % 4 === 0 ? 0.55 : 0.3}
                    />
                  ))}
                  <line x1="48" y1="10" x2="48" y2="30" className="nf-jaw" />
                  <path d="M 44 10 L 52 10 L 48 16 Z" className="nf-jaw" />
                </svg>
                <p className="notfound-title">No moulds match these filters</p>
                <p className="notfound-text">
                  {hasActiveFilters
                    ? "Try widening the size range or removing a filter."
                    : "There are no products to show yet."}
                </p>
                {hasActiveFilters && (
                  <button
                    type="button"
                    className="notfound-reset"
                    onClick={clearFilters}
                  >
                    Clear all filters
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultsPage;
